import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { ArrowRight } from 'lucide-react'

const URL = 'https://uptrack.app/blog/dns-monitoring-guide'
const TITLE = 'DNS Monitoring Guide: What to Monitor and How — Uptrack'
const DESCRIPTION =
  'DNS monitoring explained: query, server and traffic monitoring, what to check (records, nameservers, expiry, DNSSEC) and what an uptime tool can see.'

const FAQ: { q: string; a: string }[] = [
  {
    q: 'What is DNS monitoring?',
    a: 'DNS monitoring checks, on a schedule, that your domain returns the right DNS records, that the answers are fast, and that your nameservers respond. Good monitoring also warns you before the domain expires.',
  },
  {
    q: 'What is DNS query monitoring?',
    a: 'DNS query monitoring asks a resolver or a nameserver for a record, for example the A record of your domain or its MX records, and checks the answer and the response time. An external uptime tool can do this.',
  },
  {
    q: 'What is the difference between DNS server monitoring and DNS traffic monitoring?',
    a: 'Server monitoring asks whether each nameserver is up and gives the same, correct answers. Traffic monitoring counts the queries your DNS receives: how many, for which names, from where. Traffic data comes from your DNS provider or your own server logs, not from an outside check.',
  },
  {
    q: 'How often should I check DNS?',
    a: 'Often enough to catch a change before your users do. A check every minute or faster is common. Remember that public resolvers cache answers for the record TTL, so a change can take that long to reach users, while a check that asks your own nameserver sees it at once.',
  },
  {
    q: 'Can DNS monitoring detect a hijack?',
    a: 'It can detect that a record changed, for example a new A record or different nameservers, and tell you what was added and removed. It cannot tell you why it changed, so treat an unexpected change as an incident until you know.',
  },
]

export const Route = createFileRoute('/_site/blog/dns-monitoring-guide')({
  component: DnsMonitoringGuidePage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: 'description', content: DESCRIPTION },
      { property: 'og:title', content: 'DNS Monitoring Guide: What to Monitor and How' },
      {
        property: 'og:description',
        content:
          'Query, server and traffic monitoring explained. What to check in DNS, and what an uptime tool can and cannot see.',
      },
      { property: 'og:image', content: 'https://uptrack.app/og/blog-dns-monitoring-guide.png' },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: URL },
      { property: 'article:published_time', content: '2026-10-13' },
    ],
    links: [{ rel: 'canonical', href: URL }],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: 'DNS Monitoring Guide: What to Monitor and How',
          description: DESCRIPTION,
          datePublished: '2026-10-13',
          dateModified: '2026-10-13',
          author: { '@type': 'Organization', name: 'Uptrack' },
          publisher: { '@type': 'Organization', name: 'Uptrack', url: 'https://uptrack.app' },
          mainEntityOfPage: URL,
        }),
      },
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQ.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        }),
      },
    ],
  }),
})

const kinds = [
  {
    kind: 'Query monitoring',
    asks: 'Does this name return the right record, and how fast?',
    source: 'An active check: a probe asks a resolver or a nameserver',
    outside: 'Yes',
  },
  {
    kind: 'Server monitoring',
    asks: 'Is each nameserver up, and do all of them give the same answer?',
    source: 'An active check against each nameserver, plus host metrics for a server you run',
    outside: 'The answers, yes. CPU, memory and load, no',
  },
  {
    kind: 'Traffic monitoring',
    asks: 'How many queries arrive, for which names, from where?',
    source: 'Your DNS provider’s analytics or query logs, or your own server logs',
    outside: 'No',
  },
]

function DnsMonitoringGuidePage() {
  return (
    <article className="article-prose container mx-auto max-w-2xl px-4 py-16">
      <p className="mb-3 text-sm text-muted-foreground">October 13, 2026</p>
      <h1 className="font-sans mb-6 text-4xl font-semibold">
        DNS monitoring: what to monitor, and what an uptime tool can see
      </h1>

      <section className="mb-12">
        <p className="mb-4 text-lg leading-[1.7] text-body">
          <strong>DNS monitoring</strong> means checking, on a schedule, that your domain's DNS gives the right answers, gives
          them fast, and does not expire. The name covers three different jobs: <strong>DNS query monitoring</strong> (does a
          lookup return the right record), <strong>DNS server monitoring</strong> (are your nameservers up and consistent)
          and <strong>DNS traffic monitoring</strong> (how many queries do you receive). An uptime tool does the first well,
          part of the second, and none of the third.
        </p>
        <p className="mb-4 text-lg leading-[1.7] text-body">
          This guide explains each job, lists what to check, shows the failures that hurt most, and says plainly where an
          outside check stops being enough.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">Why DNS failures hide from HTTP monitors</h2>
        <p className="mb-4 text-lg leading-[1.7] text-body">
          Every request starts with a lookup. When DNS is wrong, an HTTP monitor sees a symptom: a timeout, a connection
          error or a certificate mismatch. It does not tell you the cause. Worse, some DNS failures give no HTTP error at
          all. A wrong A record can point your domain at an old server that still answers with HTTP 200. A deleted MX record
          does not touch your website, and your email stops arriving. A changed nameserver can send visitors to a page
          someone else controls.
        </p>
        <p className="text-lg leading-[1.7] text-body">
          So DNS needs its own checks, next to your <Link to="/features/uptime-monitoring" className="underline">uptime
          monitoring</Link>.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">Query, server and traffic monitoring: three different jobs</h2>
        <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Scrollable table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">Kind</th>
                <th className="pb-3 px-3 text-left font-medium text-muted-foreground">Question</th>
                <th className="pb-3 px-3 text-left font-medium text-muted-foreground">Data comes from</th>
                <th className="pb-3 pl-3 text-left font-medium text-muted-foreground">An outside uptime tool can</th>
              </tr>
            </thead>
            <tbody>
              {kinds.map((k) => (
                <tr key={k.kind} className="border-b align-top last:border-0">
                  <td className="py-3 pr-4 font-medium">{k.kind}</td>
                  <td className="py-3 px-3 text-muted-foreground">{k.asks}</td>
                  <td className="py-3 px-3 text-muted-foreground">{k.source}</td>
                  <td className="py-3 pl-3 text-muted-foreground">{k.outside}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 mb-4 text-lg leading-[1.7] text-body">
          Traffic data lives with whoever answers your DNS. Amazon Route 53 can send public hosted zone query logs to
          CloudWatch Logs, and Cloudflare shows DNS analytics in its dashboard and through a GraphQL API, with a retention
          that depends on the plan. Resolver caching means such logs show only part of what users ask, so read them as a
          sample, not a count. An outside check cannot see any of this traffic, and a tool that claims to is guessing.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">What to check</h2>
        <ul className="list-disc space-y-3 pl-6 text-lg leading-[1.7] text-body">
          <li>
            <strong>Resolution of the records that matter.</strong> The A and AAAA records of your domain and of www, the
            CNAME records of services you use, the MX records for email, and the TXT records for SPF, DKIM, DMARC and
            domain verification. Check the value, not only that an answer exists.
          </li>
          <li>
            <strong>Record changes.</strong> Keep the expected set of records and alert when it changes. Alerts that name
            what was added and removed save time.
          </li>
          <li>
            <strong>Response time.</strong> A slow lookup adds delay to every first visit. Track it per resolver or
            nameserver.
          </li>
          <li>
            <strong>Every nameserver.</strong> Ask each authoritative server directly. One dead server often causes only
            intermittent failures, which are the hardest to debug.
          </li>
          <li>
            <strong>Delegation.</strong> The NS records at the registry must match the nameservers your provider gave you.
          </li>
          <li>
            <strong>DNSSEC, if you use it.</strong> Signatures expire. When they do, validating resolvers return SERVFAIL,
            while resolvers that do not validate keep answering. The result is an outage that only some users see.
          </li>
          <li>
            <strong>Domain expiry.</strong> Registrars differ on grace periods, from none to several weeks depending on the
            registrar and the TLD, and DNS can stop soon after the expiry date. Warn at least 30 days ahead.
          </li>
          <li>
            <strong>Propagation after a change.</strong> Resolvers cache an answer for the record's TTL. Lower the TTL before
            a migration, then compare answers across resolvers with the{' '}
            <Link to="/tools/dns-propagation-checker" className="underline">DNS propagation checker</Link>.
          </li>
        </ul>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">Common DNS failures, and how they look</h2>
        <ul className="list-disc space-y-3 pl-6 text-lg leading-[1.7] text-body">
          <li>
            <strong>The domain expired.</strong> The site and email vanish together. A domain expiry monitor catches it
            weeks before.
          </li>
          <li>
            <strong>A stale record after a migration.</strong> Some visitors reach the old host. The new host looks fine
            from your laptop.
          </li>
          <li>
            <strong>Email stops after a provider change.</strong> Missing MX, SPF or DKIM records. The website stays up, so
            nothing alerts you.
          </li>
          <li>
            <strong>One resolver or one region fails.</strong> Users on one network cannot reach you. Compare answers from
            several resolvers before you blame your server.
          </li>
          <li>
            <strong>A TTL surprise.</strong> You fix a record and the old answer lives on in caches until the TTL runs out.
            Plan the TTL before you change the record.
          </li>
          <li>
            <strong>An unexpected NS or A change.</strong> It may be a mistake at your provider, or it may be a hijack. Alert
            on it and ask who changed it.
          </li>
        </ul>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">A checklist for a DNS change</h2>
        <p className="mb-4 text-lg leading-[1.7] text-body">
          Most DNS outages start with a planned change. This order avoids the usual surprises:
        </p>
        <ol className="list-decimal space-y-3 pl-6 text-lg leading-[1.7] text-body">
          <li>
            <strong>Lower the TTL first.</strong> Set a short TTL, for example 300 seconds, on the records you will change,
            then wait for the old TTL to run out before you touch the values.
          </li>
          <li>
            <strong>Make the change, then ask your own nameserver.</strong> A direct query to your authoritative server skips
            resolver caches, so you see the new value at once.
          </li>
          <li>
            <strong>Check propagation from several resolvers.</strong> Different resolvers hold the old answer for different
            lengths of time.
          </li>
          <li>
            <strong>Keep the old host running.</strong> Leave it up until the old TTL has passed, so slow caches still reach
            something that works.
          </li>
          <li>
            <strong>Raise the TTL again, and save the new record set.</strong> Add a record-change monitor with the final
            values, so the next unexpected change raises an alert.
          </li>
        </ol>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">How Uptrack covers DNS</h2>
        <p className="mb-4 text-lg leading-[1.7] text-body">
          Uptrack has four DNS-related monitor types. They are on paid plans; the free plan has HTTP and keyword monitors
          only.
        </p>
        <ul className="mb-4 list-disc space-y-3 pl-6 text-lg leading-[1.7] text-body">
          <li>
            <strong>DNS:</strong> resolve a name and check the answer against the value you expect, with an optional custom
            DNS server so you can query your own nameserver instead of a public resolver.
          </li>
          <li>
            <strong>DNS record change:</strong> watch the set of A, AAAA, CNAME, MX, TXT, NS or PTR records and alert when it
            changes, with the added and removed records listed.
          </li>
          <li>
            <strong>Domain expiry:</strong> an RDAP lookup with a WHOIS fallback for TLDs without RDAP. It alerts a set number
            of days before the domain expires (30 by default).
          </li>
          <li>
            <strong>DNS blocklist:</strong> check your host's IPv4 address against DNSBL zones such as SpamCop and Barracuda.
            Spamhaus may not answer these lookups.
          </li>
        </ul>
        <p className="mb-4 text-lg leading-[1.7] text-body">
          DNS checks run every 30 seconds on every paid plan, and an alert needs consecutive failed checks, which keeps one
          lost packet from paging you. They run from Nuremberg and Hyderabad: Germany and India. See the{' '}
          <Link to="/features/dns-monitoring" className="underline">DNS monitoring page</Link> for the full list.
        </p>
        <p className="text-lg leading-[1.7] text-body">
          Want to look before you monitor? These free tools need no sign-up:{' '}
          <Link to="/tools/dns-lookup" className="underline">DNS lookup</Link>,{' '}
          <Link to="/tools/dns-propagation-checker" className="underline">DNS propagation checker</Link>,{' '}
          <Link to="/tools/mx-lookup" className="underline">MX lookup</Link>,{' '}
          <Link to="/tools/txt-record-lookup" className="underline">TXT record lookup</Link>,{' '}
          <Link to="/tools/whois-lookup" className="underline">WHOIS lookup</Link> and the{' '}
          <Link to="/tools/domain-expiry-checker" className="underline">domain expiry checker</Link>. Background reading:{' '}
          <Link to="/glossary/dns-propagation" className="underline">what DNS propagation is</Link>.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">When an uptime tool is not enough</h2>
        <ul className="list-disc space-y-3 pl-6 text-lg leading-[1.7] text-body">
          <li>
            <strong>You need traffic numbers.</strong> Queries per second, top names and attack spikes come from your DNS
            provider's analytics or your server logs, not from outside checks.
          </li>
          <li>
            <strong>You run your own nameservers.</strong> Outside checks show the answers. For CPU, memory, query rate and
            cache hit ratio, export metrics from the DNS software and watch them in a metrics tool.
          </li>
          <li>
            <strong>You must know why a record changed.</strong> Monitoring shows that and when. The provider's audit log
            shows who.
          </li>
          <li>
            <strong>You need resolver-level views from many networks.</strong> DNS checks here run from three locations. For
            a wide look, use the propagation checker after a change.
          </li>
        </ul>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-6 text-2xl font-semibold">DNS monitoring questions</h2>
        <div className="space-y-6">
          {FAQ.map((f) => (
            <div key={f.q}>
              <h3 className="font-sans mb-1 font-semibold">{f.q}</h3>
              <p className="text-lg leading-[1.7] text-body">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="rounded-lg border border-border bg-muted p-8 text-center">
        <h2 className="font-sans mb-3 text-xl font-semibold">Monitor your DNS records and domain expiry</h2>
        <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
          DNS, record change and domain expiry monitors on paid plans from $3/mo, with a 30-day money-back guarantee.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Button size="lg" asChild>
            <Link to="/features/dns-monitoring">
              DNS monitoring
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
          <Button size="lg" variant="outline" asChild>
            <Link to="/pricing">
              See plans
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </article>
  )
}
